import { memo, useCallback, useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { DNDFileGalleryCardDragPreview } from '~/components/DNDFileGalleryCardDragPreview';
import { DNDAssetGalleryCardDragPreview } from '~/components/DndAssetDragPreview';
import { DNDBoardGalleryCardDragPreview } from '~/components/DndBoardDragPreview';
import { DndDragPreviewContainer } from '~/components/DndDragPreviewContainer';
import { ReactDndDragItemsCount } from '~/components/ReactDndDragLayer/shared/ReactDndDragItemsCount';
import { type DraggingItems, DragType } from '~/components/Shared/Drag/dragTypes';
import { hasBoardFirstItem, hasClipFirstItem } from '~/components/Shared/Drag/utils/dragTypeGuards';
import { BOARD_DRAG_PREVIEW_HEIGHT, BOARD_DRAG_PREVIEW_WIDTH } from '~/constants/dnd';
import { getClipRatio } from '~/hooks/useClipRatios';

const SINGLE_ASSET_PREVIEW_MAX = 240;
const CONTAINER_PADDING = 8;

const getThumbnailSize = (clip: Clip, thumbnailMaxSize: number): { width: number; height: number } => {
  const ratio = getClipRatio(clip);

  if (ratio >= 1) {
    return { height: Math.round(thumbnailMaxSize / ratio), width: thumbnailMaxSize };
  }

  return { height: thumbnailMaxSize, width: Math.round(thumbnailMaxSize * ratio) };
};

export type GalleryItemsDragPreviewProps = {
  items: DraggingItems<Board | Clip>;
  thumbnailMaxSize?: number;
};

export const GalleryItemsDragPreview = memo(
  ({ items, thumbnailMaxSize = SINGLE_ASSET_PREVIEW_MAX }: GalleryItemsDragPreviewProps) => {
    const { type, mixedTypes, count = 1 } = items;
    const isBoardType =
      type === DragType.boardCard ||
      type === DragType.boardNavWorkspace ||
      type === DragType.boardNavFavorites ||
      type === DragType.boardNavLibrary;
    const isFileType = type === DragType.file;

    const firstAssetThumbnailSize = useMemo(() => {
      if (!hasClipFirstItem(items)) {
        return undefined;
      }
      if (isFileType) {
        return undefined;
      }

      return getThumbnailSize(items.firstItem, thumbnailMaxSize);
    }, [items, isFileType, thumbnailMaxSize]);

    const containerStyle = useMemo(() => {
      if (isBoardType) {
        // Board ghost is always the Small card size — fixed 180×160
        return {
          height: BOARD_DRAG_PREVIEW_HEIGHT,
          width: BOARD_DRAG_PREVIEW_WIDTH,
        };
      }
      if (!firstAssetThumbnailSize) {
        return undefined;
      }

      // Asset ghost uses inset-1 (4px) on all sides — add 8px so displayed thumbnail matches firstAssetThumbnailSize
      return {
        height: firstAssetThumbnailSize.height + CONTAINER_PADDING,
        width: firstAssetThumbnailSize.width + CONTAINER_PADDING,
      };
    }, [isBoardType, firstAssetThumbnailSize]);

    const renderContent = useCallback(() => {
      switch (items.type) {
        case DragType.boardNavWorkspace:
        case DragType.boardNavFavorites:
        case DragType.boardCard:
        case DragType.boardNavLibrary:
          if (hasBoardFirstItem(items)) {
            return <DNDBoardGalleryCardDragPreview board={items.firstItem} height={BOARD_DRAG_PREVIEW_HEIGHT} />;
          }
          break;
        case DragType.asset:
        case DragType.assetPicker:
          if (hasClipFirstItem(items)) {
            return (
              <DNDAssetGalleryCardDragPreview
                asset={items.firstItem}
                height={firstAssetThumbnailSize?.height}
                width={firstAssetThumbnailSize?.width}
              />
            );
          }
          break;
        case DragType.file:
          if (hasClipFirstItem(items)) {
            return <DNDFileGalleryCardDragPreview file={items.firstItem} />;
          }
          break;
      }
      return null;
    }, [items, firstAssetThumbnailSize]);

    return (
      <div className="relative">
        {count > 2 && (
          <DndDragPreviewContainer
            className={cn(
              'absolute top-2 left-2',
              isBoardType && 'rounded-12 bg-gray-1 shadow-none ring-1 ring-gray-5 ring-inset',
            )}
            style={containerStyle}
          />
        )}
        {count > 1 && (
          <DndDragPreviewContainer
            className={cn(
              'absolute top-1 left-1',
              isBoardType && 'rounded-12 bg-gray-1 shadow-none ring-1 ring-gray-5 ring-inset',
            )}
            style={containerStyle}
          />
        )}
        <DndDragPreviewContainer
          className={cn(
            'absolute top-0 left-0 flex',
            (isBoardType || isFileType) && 'rounded-none bg-transparent shadow-none',
          )}
          style={containerStyle}
        >
          {renderContent()}
          {count > 1 && (
            <div className="absolute top-3 right-3">
              <ReactDndDragItemsCount count={count} mixedTypes={mixedTypes} type={type} />
            </div>
          )}
        </DndDragPreviewContainer>
      </div>
    );
  },
);

GalleryItemsDragPreview.displayName = 'GalleryItemsDragPreview';
